import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  Box,
  Typography,
  TablePagination,
  Chip,
  Stack,
  Avatar,
  Container,
  Tooltip,
  IconButton,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  ToggleButtonGroup,
  ToggleButton,
  Paper,
  Button,
  CircularProgress,
} from "@mui/material";
import { useTheme, alpha } from "@mui/material/styles";
import CustomButton from "../../components/common/CustomButton";
import { useLocation } from "react-router-dom";
import { jdApi } from "../../api/jobdescriptionApi";
import { candidateApi } from "../../api/candidateApi";
import { getPlanExhaustMessage } from "../../components/common/StatusDictionary";
import CustomFilter from "../../components/common/CustomFilter";
import { formatDate, formatTime } from "../../utils/dateUtils";
import { PieChart, Pie, Cell, ResponsiveContainer } from "recharts";
import CustomSnackBar from "../../components/common/CustomSnackbar";
import SkeletonLoader from "../../components/common/SkeletonLoader";
import CandidateMatchesHeader from "../../components/CandidateMatchesHeader/CandidateMatchesHeader";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import TableRowsIcon from "@mui/icons-material/TableRows";
import GridViewIcon from "@mui/icons-material/GridView";
import ViewListIcon from "@mui/icons-material/ViewList";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import UnfoldMoreIcon from "@mui/icons-material/UnfoldMore";

// ─── filter config ────────────────────────────────────────────────────────────
export const candidateFilters = [
  {
    key: "overall_score",
    label: "Overall Score",
    type: "range",
    min: 0,
    max: 100,
    step: 1,
  },
  {
    key: "final_verdict",
    label: "Verdict",
    type: "checkbox",
    options: [
      { label: "Strong Fit", value: "Strong Fit" },
      { label: "Moderate Fit", value: "Moderate Fit" },
      { label: "Weak Fit", value: "Weak Fit" },
    ],
  },
  {
    key: "candidate_status",
    label: "Candidate Status",
    type: "checkbox",
    options: [
      { label: "New", value: "NEW" },
      { label: "Scheduled", value: "SCHEDULED" },
      { label: "In Progress", value: "IN PROGRESS" },
      { label: "Attended", value: "ATTENDED" },
      { label: "Not Attended", value: "NOT ATTENDED" },
      { label: "Interrupted", value: "INTERRUPTED" },
    ],
  },
];

// ─── helpers ──────────────────────────────────────────────────────────────────
const scoreColor = (score, theme) => {
  if (score >= 80) return theme.palette.success.main;
  if (score >= 60) return theme.palette.warning.main;
  return theme.palette.error.main;
};

// Ultra-light pastel palette for beautiful profile avatars
const AVATAR_PALETTE = [
  { bg: "#EEFDF4", text: "#15803D", border: "#DCFCE7" }, // mint
  { bg: "#FFF4EA", text: "#EA580C", border: "#FED7AA" }, // peach
  { bg: "#F7F0FF", text: "#7C3AED", border: "#E9D5FF" }, // lavender
  { bg: "#FFF1F5", text: "#DB2777", border: "#FBCFE8" }, // pink
  { bg: "#FFFBE8", text: "#CA8A04", border: "#FEF08A" }, // yellow
  { bg: "#F8FAFC", text: "#475569", border: "#E2E8F0" }, // slate
];

const getAvatarColor = (name = "") => {
  const idx =
    [...name].reduce((a, c) => a + c.charCodeAt(0), 0) % AVATAR_PALETTE.length;

  return AVATAR_PALETTE[idx];
};

// ─── Inline Components ────────────────────────────────────────────────────────

const ActionMatchButton = ({ row, matchingIds, handleMatch }) => {
  const isMatching = matchingIds.has(row.id);
  const isCompleted = row.match_status === "COMPLETED";

  return (
    <Button
      variant={isCompleted ? "text" : "contained"}
      disableElevation
      disabled={isMatching || isCompleted}
      onClick={(e) => {
        e.stopPropagation();
        if (!isCompleted && !isMatching) handleMatch(row.id);
      }}
      sx={(theme) => ({
        minWidth: 0,
        width: "100%",
        height: 28,
        fontSize: "0.72rem",
        fontWeight: 600,
        textTransform: "none",
        borderRadius: "6px",
        px: 1.5,
        transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",

        // 1. ACTIVE STATE
        ...(!isCompleted &&
          !isMatching && {
          bgcolor: theme.palette.primary.main,
          color: "#fff",
          "&:hover": {
            bgcolor: theme.palette.primary.dark,
            transform: "translateY(-1px)",
            boxShadow: `0 4px 10px ${alpha(
              theme.palette.primary.main,
              0.25,
            )}`,
          },
        }),

        // 2. COMPLETED STATE (Overriding MUI disabled style)
        ...(isCompleted && {
          "&.Mui-disabled": {
            bgcolor: alpha(theme.palette.success.main, 0.08),
            color:
              theme.palette.mode === "dark"
                ? theme.palette.success.light
                : theme.palette.success.dark,
            border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`,
            fontWeight: 700,
          },
        }),

        // 3. LOADING STATE
        ...(isMatching && {
          "&.Mui-disabled": {
            bgcolor: theme.palette.action.hover,
            color: theme.palette.text.disabled,
          },
        }),

        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
      })}
    >
      {isMatching ? (
        <CircularProgress size={14} color="inherit" thickness={6} />
      ) : isCompleted ? (
        "Matched ✓"
      ) : (
        "Match"
      )}
    </Button>
  );
};

const ScoreRing = ({ score }) => {
  const theme = useTheme();
  const color = scoreColor(score, theme);
  const data = [{ value: score }, { value: 100 - score }];
  return (
    <Box sx={{ width: 36, height: 36, position: "relative", flexShrink: 0 }}>
      <ResponsiveContainer width="100%" height="100%">
        <PieChart>
          <Pie
            data={data}
            innerRadius={10}
            outerRadius={16}
            startAngle={90}
            endAngle={-270}
            dataKey="value"
            strokeWidth={0}
          >
            <Cell fill={color} />
            <Cell
              fill={theme.palette.mode === "dark" ? "#2a2a2a" : "#f0f0f0"}
            />
          </Pie>
          <text
            x="50%"
            y="50%"
            textAnchor="middle"
            dominantBaseline="middle"
            fontSize="9"
            fontWeight="700"
            fill={color}
          >
            {score}
          </text>
        </PieChart>
      </ResponsiveContainer>
    </Box>
  );
};

const ScoreBar = ({ value, theme }) => {
  const color = scoreColor(value, theme);
  return (
    <Box
      sx={{ display: "flex", alignItems: "center", gap: 0.5, width: "100%" }}
    >
      <Box
        sx={{
          flex: 1,
          height: 4,
          borderRadius: 99,
          bgcolor:
            theme.palette.mode === "dark"
              ? alpha("#fff", 0.08)
              : alpha("#000", 0.07),
          overflow: "hidden",
          minWidth: 36,
        }}
      >
        <Box
          sx={{
            width: `${value}%`,
            height: "100%",
            bgcolor: color,
            borderRadius: 99,
            transition: "width 0.4s ease",
          }}
        />
      </Box>
      <Typography
        fontSize="0.65rem"
        fontWeight={600}
        color={color}
        sx={{ minWidth: 24, textAlign: "right" }}
      >
        {value}%
      </Typography>
    </Box>
  );
};

const VerdictBadge = ({ verdict, reason }) => {
  const value = verdict || "Not Available";
  const isStrong = value.toLowerCase().includes("strong");
  const isMod = value.toLowerCase().includes("moderate");

  const styles = isStrong
    ? { bg: "#d1fae5", text: "#065f46", dot: "#10b981" }
    : isMod
      ? { bg: "#fef3c7", text: "#78350f", dot: "#f59e0b" }
      : { bg: "#fee2e2", text: "#7f1d1d", dot: "#ef4444" };

  return (
    <Tooltip title={reason || value} placement="top">
      <Box
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: 0.5,
          px: 1,
          py: 0.35,
          borderRadius: 1,
          bgcolor: styles.bg,
          cursor: "default",
          maxWidth: 130,
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            width: 6,
            height: 6,
            borderRadius: "50%",
            bgcolor: styles.dot,
            flexShrink: 0,
          }}
        />
        <Typography
          fontSize="0.7rem"
          fontWeight={700}
          color={styles.text}
          noWrap
          sx={{ letterSpacing: "0.01em" }}
        >
          {value}
        </Typography>
      </Box>
    </Tooltip>
  );
};

const MiniBar = ({ value, theme }) => {
  const color = scoreColor(value, theme);
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
      <Box
        sx={{
          flex: 1,
          height: 4,
          borderRadius: 2,
          bgcolor: theme.palette.mode === "dark" ? "#333" : "#e5e7eb",
          overflow: "hidden",
          minWidth: 48,
        }}
      >
        <Box
          sx={{
            width: `${value}%`,
            height: "100%",
            bgcolor: color,
            borderRadius: 2,
          }}
        />
      </Box>
      <Typography
        fontSize="0.72rem"
        color="text.secondary"
        sx={{ minWidth: 28 }}
      >
        {value}%
      </Typography>
    </Box>
  );
};

const VerdictChip = ({ verdict, reason }) => {
  const value = verdict || "Not Available";

  const isStrong = value.toLowerCase().includes("strong");
  const isMod = value.toLowerCase().includes("moderate");

  const sx = isStrong
    ? { bgcolor: "#dcfce7", color: "#166534" }
    : isMod
      ? { bgcolor: "#fef3c7", color: "#92400e" }
      : { bgcolor: "#fee2e2", color: "#991b1b" };

  return (
    <Tooltip title={reason || value}>
      <Chip
        label={value}
        size="small"
        sx={{
          ...sx,
          fontWeight: 600,
          fontSize: "0.72rem",
          maxWidth: 130,
          "& .MuiChip-label": {
            overflow: "hidden",
            textOverflow: "ellipsis",
          },
        }}
      />
    </Tooltip>
  );
};

const StatusChip = ({ status, statusStyle }) => (
  <Tooltip title={status}>
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        px: 0.85,
        py: 0.2,
        borderRadius: 0.75,
        bgcolor: alpha(statusStyle.bgcolor, 0.15),
        border: `1px solid ${alpha(statusStyle.bgcolor, 0.35)}`,
        maxWidth: "100%",
        overflow: "hidden",
      }}
    >
      <Typography
        fontSize="0.6rem"
        fontWeight={700}
        noWrap
        sx={{
          color: statusStyle.bgcolor,
          letterSpacing: "0.04em",
          textTransform: "uppercase",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        {status}
      </Typography>
    </Box>
  </Tooltip>
);

const SortIcon = ({ field, sortField, sortDir }) => {
  if (sortField !== field)
    return <UnfoldMoreIcon sx={{ fontSize: 13, opacity: 0.3 }} />;
  return sortDir === "asc" ? (
    <KeyboardArrowUpIcon sx={{ fontSize: 13, color: "primary.main" }} />
  ) : (
    <KeyboardArrowDownIcon sx={{ fontSize: 13, color: "primary.main" }} />
  );
};

// ─── Custom Table ─────────────────────────────────────────────────────────────
const CandidateTable = ({
  rows,
  theme,
  getStatusStyle,
  handleMenuOpen,
  handleMatch,
  matchingIds,
  loadingCandidateId,
  resumeLoadingId,
}) => {
  const [sortField, setSortField] = useState(null);
  const [sortDir, setSortDir] = useState("desc");

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortDir("desc");
    }
  };

  const sorted = useMemo(() => {
    if (!sortField) return rows;
    return [...rows].sort((a, b) => {
      let av, bv;
      if (sortField === "name") {
        av = a.name || "";
        bv = b.name || "";
      } else if (sortField === "agg_score") {
        av = a.agg_score || 0;
        bv = b.agg_score || 0;
      } else if (sortField === "skills") {
        av = a.breakdown?.skills || 0;
        bv = b.breakdown?.skills || 0;
      } else if (sortField === "experience") {
        av = a.breakdown?.experience || 0;
        bv = b.breakdown?.experience || 0;
      } else if (sortField === "qualification") {
        av = a.breakdown?.qualification || 0;
        bv = b.breakdown?.qualification || 0;
      } else if (sortField === "verdict") {
        av = a.verdict || "";
        bv = b.verdict || "";
      } else if (sortField === "status") {
        av = a.status || "";
        bv = b.status || "";
      } else return 0;

      if (typeof av === "number") return sortDir === "asc" ? av - bv : bv - av;
      return sortDir === "asc" ? av.localeCompare(bv) : bv.localeCompare(av);
    });
  }, [rows, sortField, sortDir]);

  const isDark = theme.palette.mode === "dark";
  const headerBg = isDark ? alpha("#fff", 0.04) : "#f8f9fb";
  const tableBorderColor = alpha(theme.palette.divider, 0.6);
  const rowHover = isDark ? alpha("#fff", 0.05) : alpha("#000", 0.03);

  return (
    <Box
      sx={{
        border: `1px solid ${tableBorderColor}`,
        borderRadius: 2,
        overflow: "hidden",
        width: "100%",
        bgcolor: "background.paper",
        boxShadow: isDark ? "none" : "0 4px 12px rgba(0,0,0,0.02)",
      }}
    >
      <Box sx={{ overflowX: "auto" }}>
        <Box
          component="table"
          sx={{
            width: "100%",
            minWidth: 1100,
            borderCollapse: "collapse",
            tableLayout: "fixed",
          }}
        >
          <Box
            component="thead"
            sx={{
              position: "sticky",
              top: 0,
              zIndex: 2,
              bgcolor: headerBg,
            }}
          >
            <Box component="tr">
              {[
                { label: "Candidate", field: "name", w: "18%" },
                { label: "Score", field: "agg_score", w: "6%" },
                { label: "Skills", field: "skills", w: "9%" },
                { label: "Exp.", field: "experience", w: "9%" },
                { label: "Qual.", field: "qualification", w: "9%" },
                { label: "Verdict", field: "verdict", w: "12%" },
                { label: "Status", field: "status", w: "9%" },
                { label: "Scheduled", field: null, w: "8%" },
                { label: "Expires", field: null, w: "8%" },
                { label: "Match", field: null, w: "8%" },
                { label: "", field: null, w: "4%" },
              ].map(({ label, field, w }, i) => (
                <Box
                  component="th"
                  key={i}
                  onClick={field ? () => handleSort(field) : undefined}
                  sx={{
                    width: w,
                    px: 1.5,
                    py: 1.25,
                    textAlign: "left",
                    fontWeight: 700,
                    fontSize: "0.65rem",
                    letterSpacing: "0.06em",
                    textTransform: "uppercase",
                    color:
                      sortField === field ? "primary.main" : "text.secondary",
                    cursor: field ? "pointer" : "default",
                    userSelect: "none",
                    borderBottom: `1px solid ${tableBorderColor}`,
                    borderRight:
                      i === 10 ? "none" : `1px solid ${tableBorderColor}`,
                    whiteSpace: "nowrap",
                    "&:hover": field
                      ? {
                        color: "primary.main",
                        bgcolor: isDark
                          ? alpha("#fff", 0.05)
                          : alpha("#000", 0.02),
                      }
                      : {},
                  }}
                >
                  <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.3 }}>
                    {label}
                    {field && (
                      <SortIcon field={field} sortField={sortField} sortDir={sortDir} />
                    )}
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>

          <Box component="tbody">
            {sorted.length === 0 ? (
              <Box component="tr">
                <Box component="td" colSpan={11} sx={{ py: 6, textAlign: "center" }}>
                  <Typography fontSize="0.85rem" color="text.disabled">
                    No candidates found
                  </Typography>
                </Box>
              </Box>
            ) : (
              sorted.map((row, idx) => {
                const status = (row.status || "NEW").toUpperCase();
                const statusStyle = getStatusStyle(status);
                const score = Math.round(row.agg_score || 0);
                const isLast = idx === sorted.length - 1;
                const av = getAvatarColor(row.name);

                const tdStyles = {
                  px: 1.5,
                  py: 1,
                  verticalAlign: "middle",
                  borderRight: `1px solid ${tableBorderColor}`,
                  overflow: "hidden",
                };

                return (
                  <Box
                    component="tr"
                    key={row.id}
                    sx={{
                      borderBottom: isLast ? "none" : `1px solid ${tableBorderColor}`,
                      transition: "background 0.15s ease",
                      "&:hover": { bgcolor: rowHover },
                    }}
                  >
                    {/* Candidate */}
                    <Box component="td" sx={tdStyles}>
                      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ minWidth: 0 }}>
                        <Avatar
                          sx={{
                            width: 38,
                            height: 38,
                            background: `${av.bg} !important`,
                            backgroundImage: "none !important",
                            color: `${av.text} !important`,
                            border: `1px solid ${av.border}`,
                            fontWeight: 800,
                            fontSize: "1rem",
                            boxShadow: "none",
                            "&::before": { display: "none" },
                            "&:hover": { transform: "scale(1.04)", background: `${av.bg} !important` },
                          }}
                        >
                          {(row.name || "?")[0]?.toUpperCase()}
                        </Avatar>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography fontWeight={600} fontSize="0.78rem" noWrap sx={{ color: "text.primary" }}>
                            {row.name || "Unknown"}
                          </Typography>
                          <Typography fontSize="0.65rem" color="text.secondary" noWrap>
                            {row.email || "—"}
                          </Typography>
                        </Box>
                      </Stack>
                    </Box>

                    <Box component="td" sx={tdStyles}><ScoreRing score={score} /></Box>
                    <Box component="td" sx={tdStyles}><ScoreBar value={row.breakdown?.skills ?? 0} theme={theme} /></Box>
                    <Box component="td" sx={tdStyles}><ScoreBar value={row.breakdown?.experience ?? 0} theme={theme} /></Box>
                    <Box component="td" sx={tdStyles}><ScoreBar value={row.breakdown?.qualification ?? 0} theme={theme} /></Box>
                    <Box component="td" sx={tdStyles}><VerdictBadge verdict={row.verdict} reason={row.reason} /></Box>
                    <Box component="td" sx={tdStyles}><StatusChip status={status} statusStyle={statusStyle} /></Box>

                    {/* Scheduled */}
                    <Box component="td" sx={tdStyles}>
                      {!row.scheduled_at ? (
                        <Typography fontSize="0.7rem" color="text.disabled">—</Typography>
                      ) : (
                        <Box>
                          <Typography fontSize="0.7rem" fontWeight={600}>{formatDate(row.scheduled_at)}</Typography>
                          <Typography fontSize="0.62rem" color="text.secondary">{formatTime(row.scheduled_at)}</Typography>
                        </Box>
                      )}
                    </Box>

                    {/* Expires */}
                    <Box component="td" sx={tdStyles}>
                      {!row.expired_at ? (
                        <Typography fontSize="0.7rem" color="text.disabled">—</Typography>
                      ) : (
                        <Box>
                          <Typography fontSize="0.7rem" fontWeight={600}>{formatDate(row.expired_at)}</Typography>
                          <Typography fontSize="0.62rem" color="text.secondary">{formatTime(row.expired_at)}</Typography>
                        </Box>
                      )}
                    </Box>

                    {/* Match Action */}
                    <Box component="td" sx={tdStyles}>
                      <ActionMatchButton row={row} matchingIds={matchingIds} handleMatch={handleMatch} />
                    </Box>

                    {/* More Menu Actions */}
                    <Box component="td" sx={{ ...tdStyles, borderRight: "none", textAlign: "center" }}>
                      {(loadingCandidateId === row.id || resumeLoadingId === row.id) ? (
                        <CircularProgress size={18} thickness={4} color="primary" sx={{ display: "block", m: "auto" }} />
                      ) : (
                        <IconButton
                          size="small"
                          onClick={(e) => handleMenuOpen(e, row)}
                          sx={{
                            flexShrink: 0, ml: 1, alignSelf: "flex-start",
                            width: 28, height: 28,
                            "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.08) },
                          }}
                        >
                          <MoreVertIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                      )}
                    </Box>
                  </Box>
                );
              })
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

// ─── page ─────────────────────────────────────────────────────────────────────
const ResumeMatcherLLMPage = () => {
  const theme = useTheme();
  const location = useLocation();
  const jdId = location.state?.jdId;

  const STATUS_CONFIG = {
    NEW: { bgcolor: theme.palette.primary.main, color: "#fff" },
    SCHEDULED: { bgcolor: theme.palette.secondary.main, color: "#003040" },
    RESCHEDULED: { bgcolor: theme.palette.secondary.main, color: "#003040" },
    INPROGRESS: { bgcolor: theme.palette.warning.main, color: "#000" },
    "IN PROGRESS": { bgcolor: theme.palette.warning.main, color: "#000" },
    TERMINATED: { bgcolor: theme.palette.error.main, color: "#fff" },
    COMPLETED: { bgcolor: theme.palette.success.main, color: "#fff" },
    ATTENDED: { bgcolor: theme.palette.success.main, color: "#fff" },
    INTERRUPTED: { bgcolor: theme.palette.error.main, color: "#fff" },
    NOT_ATTENDED: { bgcolor: theme.palette.error.main, color: "#fff" },
    "NOT ATTENDED": { bgcolor: theme.palette.error.main, color: "#fff" },
  };
  const getStatusStyle = (s) =>
    STATUS_CONFIG[(s || "").toUpperCase()] || {
      bgcolor: "#9e9e9e",
      color: "#fff",
    };

  // ── state
  const [jdTitle, setJdTitle] = useState("");
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchValue, setSearchValue] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(5);

  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  const [inviteLoading, setInviteLoading] = useState(false);
  const [loadingCandidateId, setLoadingCandidateId] = useState(null);
  const [resumeLoadingId, setResumeLoadingId] = useState(null);
  const [matchingIds, setMatchingIds] = useState(new Set());
  const [viewMode, setViewMode] = useState("table");
  const [lastSynced, setLastSynced] = useState("");

  const [anchorEl, setAnchorEl] = useState(null);
  const [anchorPosition, setAnchorPosition] = useState(null);
  const [menuRow, setMenuRow] = useState(null);
  const [reInviteOpen, setReInviteOpen] = useState(false);
  const [reInviteComment, setReInviteComment] = useState("");

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    type: "info",
  });

  const [filters, setFilters] = useState([{ property: "jd_id", value: jdId }]);
  const [isFilterApplied, setIsFilterApplied] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  // ── derived
  const values = useMemo(() => {
    const obj = {};
    filters.forEach(({ property, value, operator }) => {
      if (operator) {
        obj[property] = { value: value?.value ?? value, operator };
      } else if (Array.isArray(obj[property])) {
        obj[property].push(value);
      } else if (!obj[property]) {
        obj[property] = [value];
      } else {
        obj[property] = [obj[property], value];
      }
    });
    return obj;
  }, [filters]);

  const buildFilterPayload = (filters) => {
    const grouped = {};
    filters.forEach(({ property, value, operator }) => {
      const actualValue = value?.value ?? value;
      if (!grouped[property]) {
        grouped[property] = { property, value: [], ...(operator && { operator }) };
      }
      grouped[property].value.push(actualValue);
    });
    return Object.values(grouped).map((item) => ({
      ...item,
      value: item.value.length === 1 ? item.value[0] : item.value,
    }));
  };

  const stats = useMemo(
    () => ({
      total: candidates.length,
      strong: candidates.filter((c) => c.verdict?.toLowerCase().includes("strong")).length,
      moderate: candidates.filter((c) => c.verdict?.toLowerCase().includes("moderate")).length,
      weak: candidates.filter((c) => c.verdict?.toLowerCase().includes("weak")).length,
    }),
    [candidates]
  );

  const filteredCandidates = useMemo(() => {
    if (!searchValue) return candidates;
    const lower = searchValue.toLowerCase();
    return candidates.filter(
      (c) => c.name?.toLowerCase().includes(lower) || c.email?.toLowerCase().includes(lower)
    );
  }, [candidates, searchValue]);

  // ── map
  const mapCandidate = (c) => ({
    id: c.candidate_id,
    name: c.name || "Unknown",
    email: c.email || "-",
    breakdown: {
      skills: c.skills_score ?? 0,
      experience: c.experience_score ?? 0,
      qualification: c.education_score ?? 0,
    },
    agg_score: c.overall_score ?? 0,
    status: c.candidate_status || "NEW",
    match_status: c.match_status || (c.final_verdict ? "COMPLETED" : "NOT_STARTED"),
    verdict: c.final_verdict,
    reason: c.reason,
    scheduled_at: c.interview_startAt ?? null,
    expired_at: c.interview_expireAt ?? null,
  });

  // ── Unified Data Fetching (Solves old caching bug)
  const fetchCurrentPage = async (isPolling = false) => {
    try {
      if (!isPolling) setLoading(true);
      let response;

      // Always fetch from the correct endpoint based on current filter state
      if (isFilterApplied) {
        const payload = buildFilterPayload(filters);
        response = await jdApi.getJDMatchFilter({
          payload: { filter: payload },
          page,
          page_size: pageSize,
        });
      } else {
        response = await jdApi.getJDMatchResults({
          jdId,
          page,
          page_size: pageSize,
        });
      }

      const { status, message, data } = response || {};

      if (status === 200 && data) {
        if (data.job_title) setJdTitle(data.job_title);
        const mappedResults = (data.results || []).map(mapCandidate);
        setCandidates(mappedResults);
        setTotal(data?.pagination?.total ?? 0);
        setTotalPages(data?.pagination?.total_pages ?? 1);
        setLastSynced("just now");
        return mappedResults; // Used strictly for polling verification
      } else if (!isPolling) {
        if (status === 0) setSnackbar({ open: true, message: "Network error.", type: "warning" });
        else setSnackbar({ open: true, message: message || "Failed to fetch.", type: "error" });
      }
    } catch (err) {
      console.error(err);
      if (!isPolling) setSnackbar({ open: true, message: "Something went wrong", type: "error" });
    } finally {
      if (!isPolling) setLoading(false);
    }
    return null;
  };

  // Only trigger main fetch when page, pageSize, JD, or refresh trigger changes
  useEffect(() => {
    if (jdId) {
      fetchCurrentPage();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize, refreshTrigger, jdId]);

  // ── filter handlers
  const handleChange = useCallback((key, value, mode, operator = "equal") => {
    if (key === "jd_id") return;
    setFilters((prev) => {
      let updated = [...prev];
      if (mode === "single") {
        updated = updated.filter((f) => f.property !== key);
        updated.push({ property: key, value, operator });
      } else {
        const exists = updated.some((f) => f.property === key && f.value === value);
        if (exists) updated = updated.filter((f) => !(f.property === key && f.value === value));
        else updated.push({ property: key, value });
      }
      if (!updated.some((f) => f.property === "jd_id") && jdId)
        updated.push({ property: "jd_id", value: jdId });
      return updated;
    });
  }, [jdId]);

  const handleApply = () => {
    setIsFilterApplied(true);
    setPage(1);
    setRefreshTrigger((prev) => prev + 1); // Triggers the unified fetch
  };

  const handleClear = () => {
    setFilters([{ property: "jd_id", value: jdId }]);
    setIsFilterApplied(false);
    setPage(1);
    setRefreshTrigger((prev) => prev + 1); // Triggers the unified fetch
  };

  const handleSearch = (value) => setSearchValue(value);

  // ── invite
  const handleInvite = async (selectedCand = null) => {
    let payload = null;
    let inviteCount = 0;
    try {
      if (selectedCand && (selectedCand.status || "").toUpperCase() === "NEW") {
        setLoadingCandidateId(selectedCand.id);
        payload = {
          isAlone: true,
          jd_details: { jd_id: jdId },
          candidate_details: [{
            cand_id: selectedCand.id,
            cand_name: selectedCand.name,
            email: selectedCand.email,
            agg_score: selectedCand.agg_score,
            cand_status: selectedCand.status,
          }],
        };
        inviteCount = 1;
      } else {
        setInviteLoading(true);
        const toInvite = candidates.filter((c) => (c.status || "").toUpperCase() === "NEW");
        if (toInvite.length === 0) {
          setSnackbar({ open: true, message: "No eligible NEW candidates to invite.", type: "warning" });
          return;
        }
        payload = {
          isAlone: false,
          jd_details: { jd_id: jdId },
          candidate_details: toInvite.map((c) => ({
            cand_id: c.id,
            cand_name: c.name,
            email: c.email,
            agg_score: c.agg_score,
            cand_status: c.status,
          })),
        };
        inviteCount = toInvite.length;
      }
      const response = await candidateApi.getInvitations(payload);
      const { status, data, message } = response || {};
      if (status === 200) {
        setSnackbar({ open: true, message: message || `${inviteCount} invitation(s) sent!`, type: "success" });
        setRefreshTrigger(prev => prev + 1);
      } else if (status === 403 && data?.message) {
        setSnackbar({ open: true, message: getPlanExhaustMessage(data.message) || data.message || "Invitation failed.", type: "error" });
      }
    } catch (err) {
      setSnackbar({ open: true, message: getPlanExhaustMessage(err?.response?.data?.message) || err?.response?.data?.message || "Failed to send invitations.", type: "error" });
    } finally {
      setLoadingCandidateId(null);
      setInviteLoading(false);
      handleMenuClose();
    }
  };

  // ── resume
  const handleViewResume = async (candidate_id) => {
    try {
      setResumeLoadingId(candidate_id);
      const response = await candidateApi.viewResume({ candidate_id });
      const { status, data } = response;
      if (status === 200 && data) window.open(data.download_url, "_blank");
    } catch {
      setSnackbar({ open: true, message: "Failed to open resume.", type: "error" });
    } finally {
      setResumeLoadingId(null);
      handleMenuClose();
    }
  };

  // ── match
  const handleMatch = async (candidateId) => {
    if (matchingIds.has(candidateId)) return;

    try {
      setMatchingIds((prev) => new Set(prev).add(candidateId));

      const response = await candidateApi.match({ job_id: jdId, candidate_id: candidateId });

      if (response?.status !== 200) {
        setSnackbar({ open: true, type: "error", message: response?.message || "Failed to start matching" });
        return;
      }

      let matched = false;
      for (let attempt = 0; attempt < 30; attempt++) {
        await new Promise((r) => setTimeout(r, 1500));

        // This unified call seamlessly respects filters or un-filtered modes quietly
        const latestResults = await fetchCurrentPage(true);

        const found = (latestResults || []).find(
          (r) => r.id === candidateId &&
            (r.match_status === "COMPLETED" || r.verdict || r.agg_score > 0)
        );

        if (found) {
          matched = true;
          break;
        }
      }

      if (matched) {
        setSnackbar({ open: true, type: "success", message: "Matching completed" });
      } else {
        setSnackbar({ open: true, type: "warning", message: "Matching is taking longer than expected. It will update in the background." });
      }
    } catch {
      setSnackbar({ open: true, type: "error", message: "Matching failed" });
    } finally {
      setMatchingIds((prev) => {
        const next = new Set(prev);
        next.delete(candidateId);
        return next;
      });
    }
  };

  // ── menu
  const handleMenuOpen = (event, row) => {
    event.stopPropagation();
    const rect = event.currentTarget.getBoundingClientRect();
    setAnchorPosition({ top: rect.bottom, left: rect.left });
    setAnchorEl(event.currentTarget);
    setMenuRow(row);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setAnchorPosition(null);
  };

  const closeReInviteDialog = () => {
    setReInviteOpen(false);
    setMenuRow(null);
    setReInviteComment("");
  };

  const handleOpenReInvite = (row) => {
    const s = (row.status || "").toUpperCase().trim();
    if (["NEW", "COMPLETED", "ATTENDED"].includes(s)) {
      setSnackbar({ open: true, message: `Cannot re-invite ${s} candidates.`, type: "warning" });
      handleMenuClose();
      return;
    }
    setMenuRow(row);
    setReInviteComment("");
    setReInviteOpen(true);
    setAnchorEl(null);
  };

  const handleReInviteSubmit = async () => {
    if (!reInviteComment?.trim()) {
      setSnackbar({ open: true, message: "Comment is mandatory.", type: "warning" });
      return;
    }
    if (!menuRow) return;

    setInviteLoading(true);
    try {
      const payload = {
        jd_id: jdId,
        candidate: {
          cand_id: menuRow?.id,
          cand_name: menuRow?.name,
          email: menuRow?.email,
          agg_score: menuRow?.agg_score ?? 0,
          cand_status: menuRow?.status,
        },
        comment: reInviteComment.trim(),
      };
      const response = await candidateApi.resendInvitation(payload);
      const status = response?.status;
      const message = response?.data?.message || response?.message;
      if (status === 200) {
        setSnackbar({ open: true, message: message || "Invitation resent successfully.", type: "success" });
        setReInviteOpen(false);
        setRefreshTrigger(prev => prev + 1);
      } else {
        setSnackbar({ open: true, message: getPlanExhaustMessage(message) || message || "Failed to resend invitation.", type: "error" });
      }
    } catch (err) {
      setSnackbar({ open: true, message: err?.response?.data?.message || err?.message || "Error resending invitation.", type: "error" });
    } finally {
      setInviteLoading(false);
    }
  };

  // ── Menu Action Status Variables
  const menuRowStatus = (menuRow?.status || "").toUpperCase().trim();
  const isReinviteDisabled = ["NEW", "COMPLETED", "ATTENDED"].includes(menuRowStatus);

  // ── grid view
  const renderGridView = () => (
    <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(210px, 1fr))", gap: 1.5 }}>
      {filteredCandidates.map((row) => {
        const status = (row.status || "NEW").toUpperCase();
        const style = getStatusStyle(status);
        const score = Math.round(row.agg_score || 0);
        const av = getAvatarColor(row.name);

        return (
          <Paper
            key={row.id}
            elevation={0}
            sx={{
              p: 2,
              bgcolor: "background.paper",
              borderRadius: 2.5,
              border: `1px solid ${theme.palette.divider}`,
              "&:hover": {
                borderColor: theme.palette.primary.main,
                boxShadow: `0 4px 16px ${alpha(theme.palette.primary.main, 0.1)}`,
              },
              transition: "all 0.2s ease",
              display: "flex", flexDirection: "column", justifyContent: "space-between",
            }}
          >
            <Box>
              <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1} mb={1.5} sx={{ width: "100%", overflow: "hidden" }}>
                <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 0, overflow: "hidden" }}>
                  <Avatar sx={{ width: 36, height: 36, flexShrink: 0, background: `${av.bg} !important`, color: `${av.text} !important`, border: `1px solid ${av.border}`, fontWeight: 800 }}>
                    {(row.name || "?")[0]?.toUpperCase()}
                  </Avatar>
                  <Box sx={{ minWidth: 0, overflow: "hidden" }}>
                    <Typography fontSize="0.78rem" fontWeight={600} noWrap sx={{ overflow: "hidden", textOverflow: "ellipsis" }}>{row.name}</Typography>
                    <Typography fontSize="0.65rem" color="text.secondary" noWrap sx={{ overflow: "hidden", textOverflow: "ellipsis" }}>{row.email}</Typography>
                  </Box>
                </Stack>
                <IconButton size="small" onClick={(e) => handleMenuOpen(e, row)} sx={{ flexShrink: 0, m: -0.5 }}>
                  <MoreVertIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Stack>
              <Stack direction="row" alignItems="center" spacing={1.5} mb={1.5}>
                <ScoreRing score={score} />
                <Box>
                  <Typography fontSize="1.1rem" fontWeight={800} color={scoreColor(score, theme)} lineHeight={1}>{score}%</Typography>
                  <Typography fontSize="0.65rem" color="text.secondary" sx={{ textTransform: "uppercase", letterSpacing: "0.05em", mt: 0.2 }}>overall</Typography>
                </Box>
              </Stack>
              <Stack spacing={0.75} mb={2}>
                {[
                  { label: "Skills", value: row.breakdown?.skills ?? 0 },
                  { label: "Exp.", value: row.breakdown?.experience ?? 0 },
                  { label: "Qual.", value: row.breakdown?.qualification ?? 0 },
                ].map(({ label, value }) => (
                  <Stack key={label} direction="row" alignItems="center" spacing={1}>
                    <Typography fontSize="0.65rem" fontWeight={600} color="text.secondary" sx={{ width: 35, flexShrink: 0 }}>{label}</Typography>
                    <ScoreBar value={value} theme={theme} />
                  </Stack>
                ))}
              </Stack>
            </Box>
            <Box sx={{ pt: 1.5, borderTop: `1px dashed ${theme.palette.divider}` }}>
              <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1.5}>
                <VerdictBadge verdict={row.verdict} reason={row.reason} />
                <StatusChip status={status} statusStyle={style} />
              </Stack>
              <ActionMatchButton row={row} matchingIds={matchingIds} handleMatch={handleMatch} />
            </Box>
          </Paper>
        );
      })}
    </Box>
  );

  // ── compact view
  const renderCompactView = () => (
    <Paper elevation={0} sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 2, bgcolor: "background.paper", overflow: "hidden", boxShadow: theme.palette.mode === "dark" ? "none" : "0 4px 12px rgba(0,0,0,0.02)" }}>
      <Box sx={{ overflowX: "auto", width: "100%" }}>
        <Box sx={{ minWidth: 1080 }}>
          <Box sx={{ display: "grid", gridTemplateColumns: "minmax(260px,2.8fr) 70px 90px 90px 90px minmax(140px,1.2fr) 110px 110px 40px", gap: 1, px: 2, py: 1, bgcolor: theme.palette.mode === "dark" ? alpha("#fff", 0.04) : "#f8f9fb", borderBottom: `1px solid ${theme.palette.divider}` }}>
            {["Candidate", "Score", "Skills", "Exp", "Qual", "Verdict", "Status", "Match", ""].map((h, i) => (
              <Typography key={i} fontSize="0.65rem" fontWeight={700} color="text.secondary" sx={{ textTransform: "uppercase", letterSpacing: "0.06em" }}>{h}</Typography>
            ))}
          </Box>
          {filteredCandidates.length === 0 ? (
            <Box sx={{ py: 5, textAlign: "center" }}>
              <Typography color="text.disabled" fontSize="0.85rem">No candidates found</Typography>
            </Box>
          ) : (
            filteredCandidates.map((row, idx) => {
              const status = (row.status || "NEW").toUpperCase();
              const style = getStatusStyle(status);
              const score = Math.round(row.agg_score || 0);
              return (
                <Box key={row.id} sx={{ display: "grid", gridTemplateColumns: "minmax(260px,2.8fr) 70px 90px 90px 90px minmax(140px,1.2fr) 110px 110px 40px", gap: 1, px: 2, py: 1, alignItems: "center", borderBottom: idx < filteredCandidates.length - 1 ? `1px solid ${theme.palette.divider}` : "none", "&:hover": { bgcolor: theme.palette.action.hover } }}>
                  <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 0, overflow: "hidden" }}>
                    <Avatar sx={{ width: 32, height: 32, flexShrink: 0, background: `${getAvatarColor(row.name).bg} !important`, color: `${getAvatarColor(row.name).text} !important`, border: `1px solid ${getAvatarColor(row.name).border}` }}>
                      {(row.name || "?")[0]?.toUpperCase()}
                    </Avatar>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography noWrap fontWeight={600} fontSize="0.78rem">{row.name}</Typography>
                      <Typography noWrap fontSize="0.65rem" color="text.secondary">{row.email}</Typography>
                    </Box>
                  </Stack>
                  <Typography fontSize="0.82rem" fontWeight={700} color={scoreColor(score, theme)}>{score}%</Typography>
                  <MiniBar value={row.breakdown?.skills ?? 0} theme={theme} />
                  <MiniBar value={row.breakdown?.experience ?? 0} theme={theme} />
                  <MiniBar value={row.breakdown?.qualification ?? 0} theme={theme} />
                  <VerdictChip verdict={row.verdict} reason={row.reason} />
                  <Chip label={status} size="small" sx={{ fontSize: "0.6rem", fontWeight: 700, height: 18, ...style }} />
                  <Box sx={{ display: "flex", justifyContent: "center" }}>
                    <ActionMatchButton row={row} matchingIds={matchingIds} handleMatch={handleMatch} />
                  </Box>
                  <IconButton size="small" onClick={(e) => handleMenuOpen(e, row)}>
                    <MoreVertIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Box>
              );
            })
          )}
        </Box>
      </Box>
    </Paper>
  );

  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ mt: 3 }}>
        <SkeletonLoader type="jd-analysis" rows={6} />
      </Container>
    );
  }

  return (
    <Box sx={{ minHeight: "70vh" }}>
      <Box sx={{ px: 2, pt: 2 }}>
        <CandidateMatchesHeader
          jdTitle={jdTitle}
          stats={stats}
          inviteLoading={inviteLoading}
          onInviteAll={() => handleInvite()}
          onRefresh={() => setRefreshTrigger(prev => prev + 1)}
          lastSynced={lastSynced}
        />

        {/* ── toolbar ── */}
        <Box sx={{ mt: 2, mb: 2, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, flexWrap: "wrap" }}>
          <Box sx={{ flex: 1, minWidth: 250 }}>
            <CustomFilter
              filtersConfig={candidateFilters}
              values={values}
              onChange={handleChange}
              onApply={handleApply}
              onClear={handleClear}
              onSearch={handleSearch}
              searchValue={searchValue}
              searchPlaceholder="Search by Name or Email"
            />
          </Box>

          <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={(_, v) => { if (v) setViewMode(v); }}
            size="small"
            sx={{ "& .MuiToggleButton-root": { px: 1.5, py: 0.75, border: `1px solid ${theme.palette.divider}` } }}
          >
            <ToggleButton value="table" aria-label="table view">
              <Tooltip title="Table"><TableRowsIcon sx={{ fontSize: 18 }} /></Tooltip>
            </ToggleButton>
            <ToggleButton value="grid" aria-label="grid view">
              <Tooltip title="Grid"><GridViewIcon sx={{ fontSize: 18 }} /></Tooltip>
            </ToggleButton>
            <ToggleButton value="compact" aria-label="compact view">
              <Tooltip title="Compact"><ViewListIcon sx={{ fontSize: 18 }} /></Tooltip>
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>

        {/* ── content ── */}
        {viewMode === "table" && (
          <CandidateTable
            rows={filteredCandidates}
            theme={theme}
            getStatusStyle={getStatusStyle}
            handleMenuOpen={handleMenuOpen}
            handleMatch={handleMatch}
            matchingIds={matchingIds}
            loadingCandidateId={loadingCandidateId}
            resumeLoadingId={resumeLoadingId}
          />
        )}
        {viewMode === "grid" && renderGridView()}
        {viewMode === "compact" && renderCompactView()}
      </Box>

      {/* ── pagination ── */}
      <TablePagination
        component="div"
        count={total}
        page={page - 1}
        rowsPerPage={pageSize}
        onPageChange={(_, newPage) => setPage(newPage + 1)}
        onRowsPerPageChange={(e) => {
          setPageSize(parseInt(e.target.value, 10));
          setPage(1);
        }}
        rowsPerPageOptions={[5, 10, 25]}
      />

      {/* ── snackbar ── */}
      <CustomSnackBar
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.type || "info"}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
      />

      {/* ── action menu ── */}
      <Menu
        anchorReference="anchorPosition"
        anchorPosition={
          anchorPosition
            ? { top: anchorPosition.top, left: anchorPosition.left }
            : undefined
        }
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => {
          // Don't close while an action is in progress
          if (!loadingCandidateId && !resumeLoadingId) handleMenuClose();
        }}
      >
        {menuRowStatus === "NEW" ? (
          <MenuItem
            disabled={!!loadingCandidateId}
            onClick={() => { handleInvite(menuRow); }}
            sx={{ gap: 1, minWidth: 150 }}
          >
            {loadingCandidateId === menuRow?.id ? (
              <><CircularProgress size={14} color="inherit" /> Sending…</>
            ) : (
              "Invite"
            )}
          </MenuItem>
        ) : (
          <MenuItem
            disabled={isReinviteDisabled || !!loadingCandidateId}
            onClick={() => handleOpenReInvite(menuRow)}
          >
            Re-Invite
          </MenuItem>
        )}

        <MenuItem
          disabled={!!resumeLoadingId}
          onClick={() => { handleViewResume(menuRow?.id); }}
          sx={{ gap: 1, minWidth: 150 }}
        >
          {resumeLoadingId === menuRow?.id ? (
            <><CircularProgress size={14} color="inherit" /> Opening…</>
          ) : (
            "View Resume"
          )}
        </MenuItem>
      </Menu>

      {/* ── re-invite dialog ── */}
      <Dialog open={reInviteOpen} onClose={closeReInviteDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Re-Invite Candidate</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2, mt: 1 }}>
            Provide a mandatory reason for re-inviting this candidate.
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={3}
            variant="outlined"
            placeholder="Enter your comment here…"
            value={reInviteComment}
            onChange={(e) => setReInviteComment(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <CustomButton variant="outlined" onClick={() => setReInviteOpen(false)}>Cancel</CustomButton>
          <CustomButton
            variant="contained"
            onClick={handleReInviteSubmit}
            loading={inviteLoading}
            loadingText="Sending…"
            disabled={!reInviteComment.trim()}
          >
            Re-Invite
          </CustomButton>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ResumeMatcherLLMPage;